import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "@/lib/use-toast";
import type { GeneratedDocumentDetail, UpdateGeneratedDocumentPayload } from "@/types/generated-document";
import { generatedDocumentApi } from "./generated-document";
export const generatedDocumentQueryKeys = {
  all: ["patient-documents"] as const,
  listForPatient: (patientId: string) => [...generatedDocumentQueryKeys.all, "patient", patientId] as const,
  detail: (id: string) => [...generatedDocumentQueryKeys.all, "detail", id] as const,
  logs: (id: string) => [...generatedDocumentQueryKeys.all, "logs", id] as const,
  templates: ["patient-documents", "templates"] as const
};

// Read-only picker of the clinic's admin-managed templates (create/edit lives under Settings).
export const useDocumentTemplatesQuery = t0 => {
  const $ = _c(2);
  const enabled = t0 === undefined ? true : t0;
  let t1;
  if ($[0] !== enabled) {
    t1 = {
      queryKey: generatedDocumentQueryKeys.templates,
      queryFn: _temp,
      enabled
    };
    $[0] = enabled;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return useQuery(t1);
};
export const useGeneratedDocumentQuery = id => {
  const $ = _c(8);
  let t0;
  if ($[0] !== id) {
    t0 = generatedDocumentQueryKeys.detail(id ?? "");
    $[0] = id;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== id) {
    t1 = () => generatedDocumentApi.get(id as string);
    $[2] = id;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = Boolean(id);
  let t3;
  if ($[4] !== t0 || $[5] !== t1 || $[6] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2
    };
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return useQuery(t3);
};
export const useGeneratedDocumentsForPatientQuery = patientId => {
  const $ = _c(8);
  let t0;
  if ($[0] !== patientId) {
    t0 = generatedDocumentQueryKeys.listForPatient(patientId ?? "");
    $[0] = patientId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== patientId) {
    t1 = () => generatedDocumentApi.list(patientId as string);
    $[2] = patientId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = Boolean(patientId);
  let t3;
  if ($[4] !== t0 || $[5] !== t1 || $[6] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2
    };
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return useQuery(t3);
};
export const useGeneratedDocumentLogsQuery = id => {
  const $ = _c(8);
  let t0;
  if ($[0] !== id) {
    t0 = generatedDocumentQueryKeys.logs(id ?? "");
    $[0] = id;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== id) {
    t1 = () => generatedDocumentApi.getLogs(id as string);
    $[2] = id;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = Boolean(id);
  let t3;
  if ($[4] !== t0 || $[5] !== t1 || $[6] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2
    };
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return useQuery(t3);
};
export const useGenerateDocumentMutation = () => {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp2,
      onSuccess: data => {
        if (data) {
          queryClient.setQueryData(generatedDocumentQueryKeys.detail(data.id), data);
          queryClient.invalidateQueries({
            queryKey: generatedDocumentQueryKeys.listForPatient(data.patientId)
          });
        }
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
export const useUpdateGeneratedDocumentMutation = () => {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp3,
      onSuccess: (data, variables) => {
        if (data) {
          queryClient.setQueryData(generatedDocumentQueryKeys.detail(variables.id), data);
          queryClient.invalidateQueries({
            queryKey: generatedDocumentQueryKeys.listForPatient(data.patientId)
          });
        }
        queryClient.invalidateQueries({
          queryKey: generatedDocumentQueryKeys.logs(variables.id)
        });
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
export const useSendGeneratedDocumentEmailMutation = () => {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp4,
      onSuccess: (data, variables) => {
        if (data) {
          queryClient.setQueryData(generatedDocumentQueryKeys.detail(variables.id), data);
          queryClient.invalidateQueries({
            queryKey: generatedDocumentQueryKeys.listForPatient(data.patientId)
          });
        }
        queryClient.invalidateQueries({
          queryKey: generatedDocumentQueryKeys.logs(variables.id)
        });
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
export const useArchiveGeneratedDocumentMutation = patientId => {
  const $ = _c(10);
  const queryClient = useQueryClient();
  let t0;
  let t1;
  if ($[0] !== patientId || $[1] !== queryClient) {
    t0 = async id_0 => {
      const key = generatedDocumentQueryKeys.listForPatient(patientId);
      await queryClient.cancelQueries({
        queryKey: key
      });
      const previous = queryClient.getQueryData(key);
      queryClient.setQueryData(key, old => old ? old.filter(plan => plan.id !== id_0) : old);
      return {
        previous
      };
    };
    t1 = (_error, _id, context) => {
      if (context?.previous) {
        queryClient.setQueryData(generatedDocumentQueryKeys.listForPatient(patientId), context.previous);
      }
      toast({
        title: "Error",
        description: "Failed to archive treatment plan. Please try again.",
        variant: "error"
      });
    };
    $[0] = patientId;
    $[1] = queryClient;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  let t2;
  if ($[4] !== queryClient) {
    t2 = (_data, id_1) => {
      queryClient.removeQueries({
        queryKey: generatedDocumentQueryKeys.detail(id_1)
      });
      queryClient.removeQueries({
        queryKey: generatedDocumentQueryKeys.logs(id_1)
      });
      toast({
        title: "Success!",
        description: "Treatment plan archived.",
        variant: "success"
      });
    };
    $[4] = queryClient;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t0 || $[7] !== t1 || $[8] !== t2) {
    t3 = {
      mutationFn: _temp5,
      onMutate: t0,
      onError: t1,
      onSuccess: t2
    };
    $[6] = t0;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return useMutation(t3);
};
export const useShareGeneratedDocumentMutation = () => {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp6,
      onSuccess: (data, variables) => {
        if (data) {
          queryClient.setQueryData(generatedDocumentQueryKeys.detail(variables.id), data);
          queryClient.invalidateQueries({
            queryKey: generatedDocumentQueryKeys.listForPatient(data.patientId)
          });
        }
        queryClient.invalidateQueries({
          queryKey: generatedDocumentQueryKeys.logs(variables.id)
        });
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
function _temp() {
  return generatedDocumentApi.listTemplates();
}
function _temp2(input) {
  const {
    noteId,
    templateId
  } = typeof input === "string" ? {
    noteId: input,
    templateId: undefined
  } : input;
  return generatedDocumentApi.generate(noteId, templateId);
}
function _temp3(payload) {
  return generatedDocumentApi.update(payload.id, payload.data);
}
function _temp4(payload) {
  return generatedDocumentApi.sendEmail(payload.id, {
    subject: payload.subject,
    body: payload.body
  });
}
function _temp5(id) {
  return generatedDocumentApi.archive(id);
}
function _temp6(payload) {
  return generatedDocumentApi.share(payload.id, {
    patientMessage: payload.patientMessage
  });
}